// @ts-nocheck
import React, { useRef, useState, useEffect } from 'react'
import {
  ClearRefinements,
  Configure,
  InstantSearch,
  SearchBox,
  Stats,
} from 'react-instantsearch-dom'
import { InstantSearchProps } from 'react-instantsearch-core'
import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock'
import {
  debounceTime,
  getDefaultSearchString,
  searchStateToURL,
} from '@lib/algolia'
import QueryString from 'qs'
import AlgoliaHit from '@components/algolia/AlgoliaHit'
import AlgoliaCustomHits from '@components/algolia/algolia-custom-hits'
import NoOfFilters from '@components/algolia/NoOfFilters/NoOfFilters'
import AlgoliaFilters from '@components/collection/AlgoliaFilters'
import {
  Close,
  FilterAlt,
  Grid1,
  Grid2,
  Grid6,
  Rectangle,
} from '@components/icons'

import cn from 'clsx'

const GRID_LAYOUT_6 = 'GRID_LAYOUT_6'
const GRID_LAYOUT_4 = 'GRID_LAYOUT_4'
const GRID_LAYOUT_1 = 'GRID_LAYOUT_1'

interface AlgoliaCollectionProps extends InstantSearchProps {
  defaultCategory: string
  searchTerm?: string
  pathname?: string
}

const AlgoliaCollection = ({
  defaultCategory,
  searchClient,
  indexName,
  resultsState,
  searchState,
  searchTerm,
  pathname,
  ...rest
}: AlgoliaCollectionProps) => {
  const [hitsPerPage] = useState(36)
  const [localSearchState, setLocalSearchState] = useState(searchState)
  const [debounceState, setDebounceState] = useState<NodeJS.Timeout | null>(
    null
  )
  const [filter, setFilter] = useState(false)
  const [gridSelectionDesktop, setGridSelectionDesktop] =
    useState(GRID_LAYOUT_4)
  const [gridSelectionMobile, setGridSelectionMobile] = useState(GRID_LAYOUT_4)
  const ref = useRef() as React.MutableRefObject<HTMLDivElement>
  const filterRef = useRef() as React.MutableRefObject<HTMLDivElement>

  useEffect(() => {
    setLocalSearchState(searchState)
  }, [searchState])

  useEffect(() => {
    document.querySelector('h1')?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    })
  }, [localSearchState?.page])

  const onSearchStateChange = (newSearchState: any) => {
    if (debounceState) {
      clearTimeout(debounceState)
    }
    const newDebounceState = setTimeout(() => {
      const params = QueryString.parse(
        window?.location?.search?.replace('?', '')
      )
      const newQuery = searchStateToURL({
        ...localSearchState,
        ...newSearchState,
      })
      const newPath = `${window.location.pathname}${newQuery}`
      window.history.replaceState(
        { ...window.history.state, as: newPath, url: newPath },
        '',
        newPath
      )
    }, debounceTime)

    setLocalSearchState({ ...localSearchState, ...newSearchState })
    setDebounceState(newDebounceState)
  }

  const toggleFilter = () => {
    filter ? closeFilter() : openFilter()
  }

  const openFilter = () => {
    try {
      const currentWith = window.innerWidth
      if (currentWith < 1024) {
        disableBodyScroll(ref.current)
      }
    } catch (e: any) {
      console.log(e.error)
    }
    setFilter(true)
  }

  const closeFilter = () => {
    clearAllBodyScrollLocks()
    setFilter(false)
  }

  if (!indexName) {
    return null
  }

  const totalProducts = (
    <Stats
      translations={{
        stats(nbHits: any) {
          return `${nbHits.toLocaleString()}`
        },
      }}
    />
  )

  const defaultFilterString = getDefaultSearchString(
    defaultCategory?.length ? `collections:${defaultCategory}` : undefined
  )

  return (
    <div className="algolia_collection">
      <InstantSearch
        searchClient={searchClient}
        indexName={`${indexName}_published_at_desc`}
        resultsState={resultsState}
        searchState={localSearchState}
        onSearchStateChange={onSearchStateChange}
        stalledSearchDelay={400}
        {...rest}
      >
        <Configure
          hitsPerPage={hitsPerPage}
          filters={defaultFilterString}
          ruleContexts={[defaultCategory]}
          distinct
          facetingAfterDistinct
        />
        {searchTerm && (
          <div className="hidden">
            <SearchBox defaultRefinement={searchTerm} />
          </div>
        )}
        <div className="algolia_collection__product_list">
          <div className="flex flex-col">
            {/* filtering Panel starts */}
            <div className="sticky top-14 lg:top-19 z-20 bg-brand-white">
              <div className="wrapper py-4 flex items-center">
                <div className="flex flex-1">
                  <Grid2
                    className="hidden lg:block mr-4 cursor-pointer"
                    color={`${
                      gridSelectionDesktop === GRID_LAYOUT_4
                        ? 'inherit'
                        : '#CECECE'
                    }`}
                    onClick={() => setGridSelectionDesktop(GRID_LAYOUT_4)}
                  />
                  <Grid6
                    className="hidden lg:block  cursor-pointer"
                    color={`${
                      gridSelectionDesktop !== GRID_LAYOUT_6
                        ? '#CECECE'
                        : 'inherit'
                    }`}
                    onClick={() => setGridSelectionDesktop(GRID_LAYOUT_6)}
                  />
                  <Grid2
                    className="block lg:hidden"
                    color={`${
                      gridSelectionMobile !== GRID_LAYOUT_4
                        ? '#CECECE'
                        : 'inherit'
                    }`}
                    onClick={() => setGridSelectionMobile(GRID_LAYOUT_4)}
                  />
                  <Grid1
                    className="block lg:hidden ml-4"
                    color={`${
                      gridSelectionMobile === GRID_LAYOUT_1
                        ? 'inherit'
                        : '#CECECE'
                    }`}
                    onClick={() => setGridSelectionMobile(GRID_LAYOUT_1)}
                  />
                </div>
                <div className="body-2 text-text-disabled font-normal hidden lg:flex flex-1 justify-center">
                  {totalProducts}
                  <span className="pl-1">Products</span>
                </div>
                <div
                  className="flex items-center justify-end flex-1 cursor-pointer"
                  onClick={toggleFilter}
                >
                  <div className="ml-2">
                    <NoOfFilters hideIfZero={true} />
                  </div>
                  <span className="label-bold text-text-subdued pr-2.5">
                    FILTER & SORT
                  </span>
                  <span>
                    <FilterAlt />
                  </span>
                </div>
              </div>

              {/* desktop filter dropdown*/}
              {/*{showControls && (*/}
              <div
                className={cn(`filter-desktop pt-2 pb-6`, {
                  'expanded block bg-white': filter,
                  hidden: !filter,
                })}
              >
                <div className="wrapper" ref={filterRef}>
                  <div className="bg-brand-polar-white relative rounded-sm">
                    <div
                      className="absolute top-0 right-0 cursor-pointer p-5"
                      onClick={closeFilter}
                    >
                      <Close />
                    </div>
                    <AlgoliaFilters
                      indexName={indexName}
                      collectionHandle={defaultCategory}
                    />
                    <div className="hidden lg:flex justify-center items-center pt-12 pb-4">
                      <div className="flex text-label text-text-disabled">
                        <span className="pr-1">{totalProducts}</span>{' '}
                        <span>Results</span>
                      </div>
                      <div className="px-6">
                        <Rectangle />
                      </div>
                      <div className="hidden lg:flex items-center">
                        <NoOfFilters hideIfZero={false} />
                        <div>
                          <ClearRefinements
                            translations={{ reset: 'CLEAR ALL' }}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="pb-6 bg-brand-polar-white flex justify-center">
                      <div
                        className="flex justify-center bg-brand-chrome text-white text-label rounded-sm py-2 px-6 cursor-pointer"
                        onClick={closeFilter}
                      >
                        <span className="pr-1">Apply</span>{' '}
                        <span className="pr-1">-</span>{' '}
                        <span className="pr-1">{totalProducts}</span>{' '}
                        <span>Results</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/*)}*/}
              {/* end of desktop filter dropdown */}
              <div className="border-b lg:border-ui-light-grey"></div>
            </div>
            {/* filtering Panel ends */}
            <div className="body-2 text-text-disabled font-normal flex lg:hidden justify-center pt-2 lg:pt-0">
              {totalProducts}
              <span className="pl-1">Products</span>
            </div>
            {/* mobile filter popUp */}
            {/*{showControls && (*/}
            <div role="dialog">
              <div
                className={cn(
                  `fixed top-0 rounded-lg bg-brand-polar-white h-full w-full z-50 flex-col flex lg:hidden`,
                  {
                    block: filter,
                    hidden: !filter,
                  }
                )}
              >
                <div
                  className="absolute top-0 right-0 cursor-pointer p-5"
                  onClick={closeFilter}
                >
                  <Close />
                </div>
                <div className="z-50 flex px-5 mt-10 text-13 lg:hidden items-center">
                  <NoOfFilters hideIfZero={false} />
                  <div>
                    <ClearRefinements translations={{ reset: 'CLEAR ALL' }} />
                  </div>
                </div>
                <div
                  ref={ref}
                  className="z-50 flex-1 px-6 mt-10 mb-24 overflow-y-scroll"
                >
                  <AlgoliaFilters
                    indexName={indexName}
                    collectionHandle={defaultCategory}
                  />
                </div>
                <div className="z-50 lg:hidden fixed bottom-0 w-full pt-4 border-t border-ui-grey-25 pb-6 bg-brand-polar-white">
                  <div
                    className="flex justify-center bg-brand-chrome text-white text-label py-3 mx-6 cursor-pointer"
                    onClick={closeFilter}
                  >
                    <span className="pr-1">Apply</span>{' '}
                    <span className="pr-1">-</span>{' '}
                    <span className="pr-1">{totalProducts}</span>{' '}
                    <span>Results</span>
                  </div>
                </div>
              </div>
            </div>
            {/*)}*/}
            {/* end of mobile filter popUp */}
            <div
              className={cn(
                'infiniteHits lg:transition-all lg:ease-in-out lg:duration-500',
                { expanded: filter }
              )}
            >
              <AlgoliaCustomHits
                HitComponent={AlgoliaHit}
                totalProducts={totalProducts}
                gridSelectionDesktop={gridSelectionDesktop}
                gridSelectionMobile={gridSelectionMobile}
                pathname={pathname}
                page={localSearchState.page}
              />
            </div>
          </div>
        </div>
      </InstantSearch>
      <style jsx global>
        {`
          .ais-ClearRefinements-button {
            font-weight: 400;
            font-size: 13px;
            line-height: 24px;
            text-decoration: underline;
            text-underline-position: under;
            cursor: pointer;
            color: rgb(71, 71, 71);
          }
        `}
      </style>
      <style jsx>
        {`
          .filter-desktop {
            z-index: -1;
            visibility: hidden;
            opacity: 0;
            transition: visibility 0.5s, opacity 0.5s linear;
          }
          @media (min-width: 1024px) {
            .infiniteHits.expanded {
              margin: 0;
            }
          }
          .filter-desktop.expanded {
            z-index: 30;
            visibility: visible;
            opacity: 1;
          }
        `}
      </style>
    </div>
  )
}

export default AlgoliaCollection
